import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, GripVertical, Loader2, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

function Row({ city, i, total, move, remove, onDragStart, onDrop, tid }) {
  return (
    <li draggable onDragStart={() => onDragStart(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
      className="flex items-center gap-3 rounded border border-stone-200 bg-white px-3 py-2" data-testid={`${tid}-row-${city.slug}`}>
      <GripVertical className="h-4 w-4 text-stone-400 cursor-grab" />
      <span className="font-data text-xs text-stone-500 w-5">{i + 1}</span>
      <span className="flex-1 min-w-0 truncate text-sm text-stone-900">{city.name} <span className="text-stone-500">· {city.country}</span></span>
      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="p-1 disabled:opacity-30" data-testid={`${tid}-up-${city.slug}`}><ArrowUp className="h-4 w-4" /></button>
      <button type="button" onClick={() => move(i, 1)} disabled={i === total - 1} aria-label="Move down" className="p-1 disabled:opacity-30" data-testid={`${tid}-down-${city.slug}`}><ArrowDown className="h-4 w-4" /></button>
      <button type="button" onClick={() => remove(i)} aria-label="Remove" className="p-1 text-red-700" data-testid={`${tid}-remove-${city.slug}`}><X className="h-4 w-4" /></button>
    </li>
  );
}

export function FeaturedCitiesPicker({ endpoint = "/admin/featured-cities", heading = "Homepage featured cities", placeholder = "Add a city: type a name…", tid = "featured-picker" }) {
  const [options, setOptions] = useState([]);
  const [slugs, setSlugs] = useState([]);
  const [query, setQuery] = useState("");
  const [drag, setDrag] = useState(null);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({});

  useEffect(() => {
    api.get(endpoint).then(({ data }) => {
      setOptions(data.options); setSlugs(data.slugs); setMeta({ at: data.updated_at, by: data.updated_by });
    }).catch((e) => toast.error(e?.response?.status === 403 ? "Admins only" : "Could not load featured cities"));
  }, [endpoint]);

  const bySlug = Object.fromEntries(options.map((o) => [o.slug, o]));
  const move = (i, d) => setSlugs((s) => { const n = [...s]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const remove = (i) => setSlugs((s) => s.filter((_, j) => j !== i));
  const onDrop = (i) => {
    if (drag == null || drag === i) return;
    setSlugs((s) => { const n = [...s]; const [x] = n.splice(drag, 1); n.splice(i, 0, x); return n; });
    setDrag(null);
  };
  const matches = query.length < 2 ? [] : options.filter((o) => !slugs.includes(o.slug) && `${o.name} ${o.country}`.toLowerCase().includes(query.toLowerCase())).slice(0, 8);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(endpoint, { slugs });
      toast.success(`${heading} saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };

  return (
    <section className="border border-stone-200 rounded-lg p-5 bg-[#FBF8F3]" data-testid={tid === "featured-picker" ? "featured-cities-picker" : `${tid}-card`}>
      <h2 className="text-lg font-semibold text-stone-900">{heading}</h2>
      <p className="text-sm text-stone-600 mt-1 mb-4">Drag or use the arrows to set the rotation order (max 12).{meta.at && ` Last saved ${meta.at.slice(0, 10)}${meta.by ? ` by ${meta.by}` : ""}.`}</p>
      <ol className="space-y-2 mb-4">
        {slugs.map((s, i) => (
          <Row key={s} city={bySlug[s] || { slug: s, name: s, country: "" }} i={i} total={slugs.length} move={move} remove={remove} onDragStart={setDrag} onDrop={onDrop} tid={tid} />
        ))}
      </ol>
      <div className="relative mb-4">
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} className="w-full border border-stone-300 rounded px-3 py-2 text-sm" data-testid={`${tid}-search`} />
        {matches.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full rounded border border-stone-200 bg-white shadow-sm" data-testid={`${tid}-results`}>
            {matches.map((o) => (
              <li key={o.slug}>
                <button type="button" disabled={slugs.length >= 12} onClick={() => { setSlugs((s) => [...s, o.slug]); setQuery(""); }} className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-stone-50 disabled:opacity-40" data-testid={`${tid}-add-${o.slug}`}>
                  <Plus className="h-4 w-4" /> {o.name} <span className="text-stone-500">· {o.country}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Button onClick={save} disabled={saving || !slugs.length} data-testid={`${tid}-save`}>
        {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save order
      </Button>
    </section>
  );
}
